// Import the RTK Query methods from the React-specific entry point
import { createSlice } from '@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';

import { Point } from 'geojson';

import { PtLatLng } from '../appTypes';
import { baseQuery } from './utils';

// https://redux.js.org/tutorials/essentials/part-7-rtk-query-basics
// https://redux-toolkit.js.org/rtk-query/usage/prefetching

export type Bookmark = {
  id: string;
  center: Point;
  zoom: number;
  // eslint-disable-next-line no-restricted-globals
  name: string;
  starred: boolean;
};
export type BookmarksList = Bookmark[];

// Define our single API slice object
export const bookmarkApiSlice = createApi({
  reducerPath: 'bookmark_api',
  baseQuery,
  tagTypes: ['bookmarks'],
  // The "endpoints" represent operations and requests for this server
  endpoints: (builder) => ({
    getBookmarks: builder.query<{ bookmarks: BookmarksList }, string>({
      query: (project_slug) => {
        return `${project_slug}/bookmarks/`;
      },
      transformResponse: (rawResult: any, meta, arg) => {
        // console.log('getBookmarks query results');
        return { bookmarks: rawResult.bookmarks };
      },
      providesTags: [{ type: 'bookmarks' }],
    }),
    bookmarkUpdate: builder.mutation<
      { bookmarks: BookmarksList },
      { project_slug: string; bookmark_id: string; bookmark: Partial<Bookmark> }
    >({
      query: ({ project_slug, bookmark_id, bookmark }) => {
        return {
          url: `${project_slug}/bookmarks/${bookmark_id}/`,
          method: 'POST',
          body: bookmark,
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { bookmarks: rawResult.bookmarks };
      },
      invalidatesTags: [{ type: 'bookmarks' }],
    }),
    bookmarkDelete: builder.mutation<
      { bookmarks: BookmarksList },
      { project_slug: string; bookmark_id: string }
    >({
      query: ({ project_slug, bookmark_id }) => {
        return {
          url: `${project_slug}/bookmarks/${bookmark_id}/`,
          method: 'DELETE',
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { bookmarks: rawResult.bookmarks };
      },
      invalidatesTags: [{ type: 'bookmarks' }],
    }),
    bookmarkCreate: builder.mutation<
      { bookmarks: BookmarksList },
      { project_slug: string; bookmark_id: string; bookmark: Partial<Bookmark> }
    >({
      query: ({ project_slug, bookmark_id, bookmark }) => {
        return {
          url: `${project_slug}/bookmarks/`,
          method: 'PUT',
          body: bookmark,
        };
      },
      transformResponse: (rawResult: any, meta, arg) => {
        return { bookmarks: rawResult.bookmarks };
      },
      invalidatesTags: [{ type: 'bookmarks' }],
    }),
  }),
});

// Export the auto-generated hook for the `getDates` query endpoint
export const {
  useGetBookmarksQuery,
  useBookmarkUpdateMutation,
  useBookmarkCreateMutation,
  useBookmarkDeleteMutation,
} = bookmarkApiSlice;

const initialState: {
  displayedBookmark?: PtLatLng;
  inLocationEditMode: boolean;
} = {
  displayedBookmark: undefined,
  inLocationEditMode: false,
};

export const bookmarkSlice = createSlice({
  name: 'bookmarks',
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    setDisplayedBookmark: (state, action) => {
      // console.log('Setting displayed bookmark to ', action.payload);
      state.displayedBookmark = action.payload;
    },
    setInLocationEditMode: (state, action) => {
      // console.log('Setting location edit mode', action.payload);
      state.inLocationEditMode = action.payload;
    },
  },
});

const { actions } = bookmarkSlice;
export const { setDisplayedBookmark, setInLocationEditMode } = actions;

export function getBookmarkState(rootState) {
  return rootState[bookmarkSlice.name];
}

export const selectDisplayedBookmark = (rootState) => {
  return getBookmarkState(rootState).displayedBookmark;
};

export const selectInLocationEditMode = (rootState) => {
  return getBookmarkState(rootState).inLocationEditMode;
};
